import { DragDropModule } from "@angular/cdk/drag-drop";
import { CommonModule, DOCUMENT } from "@angular/common";
import { HttpClient, HttpClientModule } from "@angular/common/http";
import { Inject, NgModule } from "@angular/core";
import { MatTooltipModule } from "@angular/material/tooltip";
import {
  TranslateLoader,
  TranslateModule,
  TranslateService,
} from "@ngx-translate/core";
import { TranslateHttpLoader } from "@ngx-translate/http-loader";
import { NgxSkeletonLoaderModule } from "ngx-skeleton-loader";
import { environment } from "../../environments/environment";
import { CharactersAndNumbersDirective } from "./directives/characters-and-numbers.directive";
import { DragDropDirective } from "./directives/drag-drop.directive";
import { OnlyCharactersDirective } from "./directives/only-characters.directive";
import { OnlyLowerCaseCharactersDirective } from "./directives/only-lower-case-characters.directive";
import { OnlyNumbersDirective } from "./directives/only-numbers.directive";
import { PreventDoubleClickDirective } from "./directives/prevent-double-click.directive";
import { ConversationTimeFormatPipe } from "./pipe/conversation-time-format.pipe";
import { DefaultValuePipe } from "./pipe/default-value.pipe";
import { DictionaryPipe } from "./pipe/dictionary.pipe";
import { HubLabelPipe } from "./pipe/hub-label.pipe";
import { LabelDictionaryPipe } from "./pipe/label-dictionary.pipe";
import { LinkifyPipe } from "./pipe/linkify.pipe";
import { SharedTableComponent } from "./shared-table/shared-table.component";

// AoT requires an exported function for factories
export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, "./assets/i18n/", ".json");
}

@NgModule({
  declarations: [
    OnlyNumbersDirective,
    OnlyCharactersDirective,
    OnlyLowerCaseCharactersDirective,
    CharactersAndNumbersDirective,
    LabelDictionaryPipe,
    PreventDoubleClickDirective,
    DefaultValuePipe,
    DragDropDirective,
    DictionaryPipe,
    HubLabelPipe,
    ConversationTimeFormatPipe,
    SharedTableComponent,
    LinkifyPipe,
  ],
  imports: [
    CommonModule,
    MatTooltipModule,
    NgxSkeletonLoaderModule,
    HttpClientModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
    }),
    DragDropModule,
  ],
  exports: [
    CommonModule,
    MatTooltipModule,
    NgxSkeletonLoaderModule,
    OnlyNumbersDirective,
    OnlyCharactersDirective,
    OnlyLowerCaseCharactersDirective,
    CharactersAndNumbersDirective,
    LabelDictionaryPipe,
    PreventDoubleClickDirective,
    DefaultValuePipe,
    DragDropDirective,
    TranslateModule,
    DictionaryPipe,
    HubLabelPipe,
    ConversationTimeFormatPipe,
    SharedTableComponent,
    LinkifyPipe,
    DragDropModule,
  ],
})
export class SharedModule {
  constructor(
    private translate: TranslateService,
    @Inject(DOCUMENT) private document: Document,
  ) {
    const DEFAULT_LANG = environment.defaultLang;
    const SUPPORTED_LANGS = ["en", "fr", "ar"];

    // Set up language
    if (!sessionStorage.getItem("lang")) {
      sessionStorage.setItem("lang", DEFAULT_LANG);
    }

    this.translate.addLangs(SUPPORTED_LANGS);
    const currentLang =
      sessionStorage.getItem("lang") || environment.defaultLang;
    this.translate.setDefaultLang(currentLang);
    this.translate.use(currentLang);

    // Handle RTL for Arabic
    this.translate.onLangChange.subscribe(({ lang }) => {
      this.updateLayoutDirection(lang);
    });

    // Initial layout direction setup
    this.updateLayoutDirection(currentLang);
  }

  private updateLayoutDirection(lang: string) {
    const html = this.document.documentElement;

    // Reinitialisation
    html.removeAttribute("dir");
    html.classList.remove("rtl", "ltr");
    html.style.cssText = "";

    // Application RTL
    if (lang === "ar") {
      html.setAttribute("dir", "rtl");
      html.classList.add("rtl");
      // html.style.fontFamily = "'Noto Sans Arabic', sans-serif";
      html.style.direction = "rtl";
      // html.style.letterSpacing = '0';
    } else {
      html.setAttribute("dir", "ltr");
      html.classList.add("ltr");
    }

    // Forcer le recalcul des composants Material
    setTimeout(() => window.dispatchEvent(new Event("resize")), 100);
  }
}
